import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SeoHead } from '../../components/SeoHead';
import { OfferLinksColor } from '../../components/globalStyles';
import styles from './TermsPage.module.css';
import './TermsPage.css';
import structureDocument, { companyInfo } from './documentSctructure';
import structureDocumentEn from './documentStructure.en';
import structureDocumentB2B, { companyInfo as companyInfoB2B } from '../b2bTermsPage/documentStructureB2B';
import structureDocumentB2BEn from '../b2bTermsPage/documentStructureB2B.en';
import { useLocation, useNavigate, Link } from 'react-router-dom';
export default function TermsPage({ defaultSection = 'driver' }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(defaultSection);
  const tabsWrapperRef = useRef(null);
  const tabButtonRefs = useRef({});
  const [pillStyle, setPillStyle] = useState({ left: 0, width: 0, ready: false });
  const pillTransitionReadyRef = useRef(false);
  const isEnglish = (i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('en');

  const sectionRoutes = useMemo(
    () => ({
      driver: '/contract',
      stationOwner: '/b2b-contract',
    }),
    []
  );

  const sections = useMemo(
    () => ({
      driver: {
        key: 'driver',
        label: t('terms.tabs.driver'),
        title: t('offerTitle'),
        document: isEnglish ? structureDocumentEn : structureDocument,
        companyInfo,
        additionalIntro: [],
        meta: isEnglish
          ? {
              title: 'Public Offer Agreement | 220-km.com',
              description:
                'Public offer agreement for electric vehicle battery charging services at 220-km.com Charging Stations. Terms of use and the rights and obligations of the parties.',
              keywords: 'public offer agreement, charging service, accession agreement, 220-km.com, EV charging',
              url: 'https://220-km.com/contract',
              ogTitle: 'Public Offer Agreement | 220-km.com',
              ogDescription:
                'Public offer agreement for electric vehicle battery charging services at 220-km.com stations',
              twitterTitle: 'Public Offer Agreement | 220-km.com',
              twitterDescription: 'Terms of the Charging Service for EV drivers',
            }
          : {
              title: `Публічний договір надання послуг зарядки електромобілів | 220-km.com`,
              description: `Публічний договір надання послуг зарядки електромобілів на зарядних станціях 220-km.com. Умови використання, права та обов'язки сторін. ${companyInfo.fullName}.`,
              keywords: `публічний договір, договір зарядки, умови використання, 220-km.com, зарядні станції договір, оферта`,
              url: 'https://220-km.com/contract',
              ogTitle: `Публічний договір | 220-km.com`,
              ogDescription: `Публічний договір надання послуг зарядки електромобілів на зарядних станціях 220-km.com`,
              twitterTitle: `Публічний договір | 220-km.com`,
              twitterDescription: `Умови надання послуг зарядки електромобілів`,
            },
        structuredData: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: companyInfo.fullName,
          alternateName: [companyInfo.name, companyInfo.shortName],
          legalName: companyInfo.fullName,
          url: 'https://220-km.com',
          logo: 'https://220-km.com/logo512.png',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'проспект Соборності, будинок 17 офіс 2-92',
            addressLocality: 'Київ',
            postalCode: '02160',
            addressCountry: 'UA',
          },
          description:
            'Роумінгова мережа зарядних станцій для електромобілів в Україні. Мережа 220 - зарядні станції для електротранспорту.',
          taxID: companyInfo.edrpou,
        },
      },
      stationOwner: {
        key: 'stationOwner',
        label: t('terms.tabs.stationOwner'),
        title: t('terms.stationOwnerTitle'),
        document: isEnglish ? structureDocumentB2BEn : structureDocumentB2B,
        companyInfo: companyInfoB2B,
        additionalIntro: [t('terms.stationOwnerIntro')],
        meta: isEnglish
          ? {
              title: `Public Offer Agreement for Charging Station Owners | ${companyInfoB2B.shortName}`,
              description:
                'Public offer agreement of the 220-km.com network on connecting chargers and Bank API access for Charging Station Owners. Terms of cooperation with individual entrepreneur Pavlov Maksym Valeriiovych.',
              keywords: '220-km.com, public offer agreement, accession agreement, Bank API, Charging Station Owner',
              url: 'https://220-km.com/b2b-contract',
              ogTitle: 'Public Offer Agreement for Station Owners | 220-km.com',
              ogDescription:
                'Public offer agreement on connecting chargers and interaction between the Aggregator, Contractor and Charging Station Owner.',
              twitterTitle: 'Public Offer Agreement for Station Owners | 220-km.com',
              twitterDescription: 'Terms of 220-km.com technical services for Charging Station Owners.',
            }
          : {
              title: `Публічна оферта для власників станцій | ${companyInfoB2B.shortName}`,
              description:
                'Публічний договір мережі 220-km.com щодо доступу до API 220-km.com під Банк для власників зарядних станцій. Умови співпраці з ФОП Павловим Максимом Валерійовичем.',
              keywords: '220-km.com, публічна оферта, API 220-km.com, еквайринг, зарядні станції, співпраця',
              url: 'https://220-km.com/b2b-contract',
              ogTitle: 'Публічна оферта 220-km.com для партнерів',
              ogDescription:
                'Умови співпраці мережі 220-km.com для власників зарядних станцій щодо підключення API 220-km.com під Банк.',
              twitterTitle: 'Публічна оферта 220-km.com для партнерів',
              twitterDescription:
                'Договір надання технічних послуг мережі 220-km.com у сфері онлайн-оплат через API під Банк.',
            },
        structuredData: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: companyInfoB2B.fullName,
          alternateName: [companyInfoB2B.name, companyInfoB2B.shortName],
          legalName: companyInfoB2B.fullName,
          url: 'https://220-km.com',
          logo: 'https://220-km.com/logo512.png',
          address: {
            '@type': 'PostalAddress',
            streetAddress: companyInfoB2B.address,
            addressCountry: 'UA',
          },
          taxID: companyInfoB2B.edrpou,
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'customer support',
              email: companyInfoB2B.email,
              telephone: companyInfoB2B.phone,
              availableLanguage: isEnglish ? ['uk', 'en'] : ['uk'],
            },
          ],
        },
      },
    }),
    [t, isEnglish]
  );

  const locationSection = useMemo(() => {
    const entry = Object.entries(sectionRoutes).find(([, path]) => path === location.pathname);
    return entry ? entry[0] : null;
  }, [location.pathname, sectionRoutes]);

  useEffect(() => {
    if (locationSection && locationSection !== activeSection) {
      setActiveSection(locationSection);
    }
  }, [locationSection, activeSection]);

  const sectionKeys = useMemo(() => Object.keys(sections), [sections]);

  useLayoutEffect(() => {
    const updatePill = () => {
      const wrapper = tabsWrapperRef.current;
      const activeButton = tabButtonRefs.current[activeSection];
      if (!wrapper || !activeButton) {
        return;
      }

      const wrapperRect = wrapper.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      const next = {
        left: buttonRect.left - wrapperRect.left,
        width: buttonRect.width,
        ready: pillTransitionReadyRef.current,
      };

      // First measure: place the pill instantly, then enable transition for switches.
      if (!pillTransitionReadyRef.current) {
        setPillStyle({ ...next, ready: false });
        requestAnimationFrame(() => {
          pillTransitionReadyRef.current = true;
          setPillStyle(prev => ({ ...prev, ready: true }));
        });
        return;
      }

      setPillStyle({ ...next, ready: true });
    };

    updatePill();
    window.addEventListener('resize', updatePill);
    return () => window.removeEventListener('resize', updatePill);
  }, [activeSection, sectionKeys, i18n.resolvedLanguage, i18n.language]);

  const handleSelectSection = sectionKey => {
    if (sectionKey === activeSection) {
      return;
    }

    setActiveSection(sectionKey);
    const targetPath = sectionRoutes[sectionKey];
    if (targetPath && location.pathname !== targetPath) {
      navigate({ pathname: targetPath, search: '' });
    }
  };

  const currentSection = sections[activeSection] !== undefined ? sections[activeSection] : sections.driver;

  const { document, companyInfo: currentCompanyInfo, additionalIntro, title, meta, structuredData } = currentSection;
  const { body, introduction } = document;

  const introductionTexts = [...introduction, ...(additionalIntro || [])];

  const Header = ({ title: headerTitle, id, href }) => (
    <p key={id + 'header'}>
      <OfferLinksColor className={styles.offerLink} href={href}>
        <strong>{id}. </strong>
        <strong>{headerTitle}</strong>
      </OfferLinksColor>
    </p>
  );

  const IntroductionText = ({ text, index, linkCompany }) => {
    const companyName = currentCompanyInfo.name;
    if (linkCompany && typeof text === 'string') {
      // Process ТОВ «мережа 220» link
      if (text.includes(companyName)) {
        const parts = text.split(companyName);
        let result = [parts[0]];
        result.push(
          <a
            key="link-mezha-220"
            href="https://opendatabot.ua/c/45977567"
            target="_blank"
            rel="noopener noreferrer"
            style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
          >
            {companyName}
          </a>
        );

        // Process ТОВ "АВАНГАРД" link if present
        const avantgardName = 'ТОВ "АВАНГАРД"';
        const okinetName = 'ТОВ "ОКІНЕТ"';
        if (parts[1] && parts[1].includes(avantgardName)) {
          const avantgardParts = parts[1].split(avantgardName);
          result.push(avantgardParts[0]);
          result.push(
            <a
              key="link-avantgard-youcontrol"
              href="https://youcontrol.com.ua/catalog/company_details/21377944/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
            >
              {avantgardName}
            </a>
          );
          // Process ТОВ "ОКІНЕТ" link if present
          if (avantgardParts[1] && avantgardParts[1].includes(okinetName)) {
            const okinetParts = avantgardParts[1].split(okinetName);
            result.push(okinetParts[0]);
            result.push(
              <a
                key="link-okinet-youcontrol"
                href="https://youcontrol.com.ua/catalog/company_details/40776193/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
              >
                {okinetName}
              </a>
            );
            result.push(okinetParts[1]);
          } else {
            result.push(avantgardParts[1]);
          }
        } else if (parts[1] && parts[1].includes(okinetName)) {
          const okinetParts = parts[1].split(okinetName);
          result.push(okinetParts[0]);
          result.push(
            <a
              key="link-okinet-youcontrol"
              href="https://youcontrol.com.ua/catalog/company_details/40776193/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
            >
              {okinetName}
            </a>
          );
          result.push(okinetParts[1]);
        } else {
          result.push(parts[1]);
        }

        return <p key={index + 'introduction'}>{result}</p>;
      }
    }
    return <p key={index + 'introduction'}>{text}</p>;
  };

  const renderTextWithLinks = (text, linkUrl, linkLabel) => {
    if (linkUrl && linkLabel && text.includes(linkLabel)) {
      const parts = text.split(linkLabel);
      return (
        <>
          {renderTextWithLinks(parts[0])}
          <OfferLinksColor href={linkUrl} target="_blank" rel="noopener noreferrer" className={styles.offerLink}>
            {linkLabel}
          </OfferLinksColor>
          {parts[1] ? renderTextWithLinks(parts[1]) : null}
        </>
      );
    }
    const urlRegex = /(https?:\/\/[^\s]+|220-km\.com)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, index) => {
      if (!part) return null;
      const href = part.startsWith('http') ? part : part === '220-km.com' ? 'https://220-km.com' : null;
      return href ? (
        <OfferLinksColor key={index} href={href} target="_blank" rel="noopener noreferrer" className={styles.offerLink}>
          {part}
        </OfferLinksColor>
      ) : (
        part
      );
    });
  };

  const SubTexts = ({ subTexts }) =>
    subTexts.map(({ id, subText, linkUrl, linkLabel }) => (
      <p key={id + 'subText'}>
        <span>{id}</span>
        <br />
        <span>{renderTextWithLinks(subText, linkUrl, linkLabel)}</span>
      </p>
    ));

  const Text = ({ id, text, subTexts, linkUrl, linkLabel }) => (
    <div key={id + 'text'}>
      <p>
        <span>{id}</span>
        <br />
        <span>{renderTextWithLinks(text, linkUrl, linkLabel)}</span>
      </p>
      {subTexts && <SubTexts subTexts={subTexts} />}
    </div>
  );

  const BodyText = ({ title: bodyTitle, id, texts, href }) => {
    const sliceHref = href.slice(1);
    return (
      <div key={id + 'bodyText'}>
        <p id={sliceHref}>
          <strong>
            <u>
              {id}. {bodyTitle}
            </u>
          </strong>
        </p>
        {texts.map(textProps => (
          <Text key={textProps.id + 'texts'} {...textProps} />
        ))}
      </div>
    );
  };

  const renderTariffsLink = () => {
    if (activeSection !== 'stationOwner') {
      return null;
    }
    return (
      <p style={{ marginTop: '12px', marginBottom: '12px', fontSize: '16px', lineHeight: 1.45 }}>
        {t('b2bContract.tariffsLinkIntro')}{' '}
        <Link
          to={{ pathname: '/b2b', search: '' }}
          style={{
            color: '#C83DC5',
            fontWeight: '600',
            textDecoration: 'underline',
          }}
        >
          {t('b2bContract.tariffsLinkLabel')}
        </Link>
        .
      </p>
    );
  };

  return (
    <>
      <SeoHead fallbackTitle="220 km">
        <title>{meta.title}</title>
        {meta.description && <meta name="description" content={meta.description} />}
        {meta.keywords && <meta name="keywords" content={meta.keywords} />}
        {meta.url && <link rel="canonical" href={meta.url} />}
        <meta property="og:type" content="website" />
        {meta.url && <meta property="og:url" content={meta.url} />}
        {meta.ogTitle && <meta property="og:title" content={meta.ogTitle} />}
        {meta.ogDescription && <meta property="og:description" content={meta.ogDescription} />}
        <meta property="twitter:card" content="summary" />
        {meta.url && <meta property="twitter:url" content={meta.url} />}
        {meta.twitterTitle && <meta property="twitter:title" content={meta.twitterTitle} />}
        {meta.twitterDescription && <meta property="twitter:description" content={meta.twitterDescription} />}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </SeoHead>

      <Container id="contractPage" className={styles.page} style={{ marginTop: '150px', paddingBottom: '150px' }}>
        <div className={styles.tabsWrapper} role="tablist" ref={tabsWrapperRef}>
          <span
            className={`${styles.tabPill}${pillStyle.ready ? ` ${styles.tabPillReady}` : ''}`}
            style={{
              transform: `translateX(${pillStyle.left}px)`,
              width: pillStyle.width,
            }}
            aria-hidden="true"
          />
          {Object.values(sections).map(section => {
            const selected = activeSection === section.key;
            return (
              <button
                key={section.key}
                type="button"
                role="tab"
                aria-selected={selected}
                ref={node => {
                  tabButtonRefs.current[section.key] = node;
                }}
                className={selected ? `${styles.tabButton} ${styles.tabButtonActive}` : styles.tabButton}
                onClick={() => handleSelectSection(section.key)}
              >
                {section.label}
              </button>
            );
          })}
        </div>
        <div className={styles.titleWrapper}>
          <h1 id="contractPageTitle" className={styles.title}>
            {title}
          </h1>
        </div>
        <div id="contractPageContainer" className={styles.contractsBox}>
          <div id="contractPageBox" className={styles.textContainer}>
            <div className={styles.headerLinks}>
              {body.map(headerProps => (
                <Header key={headerProps.id} {...headerProps} />
              ))}
            </div>
            <div className={styles.introductionText}>
              {introductionTexts.map((text, index) => (
                <IntroductionText key={index} text={text} index={index} linkCompany={activeSection === 'driver'} />
              ))}
            </div>

            {body.map(bodyProps => (
              <React.Fragment key={bodyProps.id}>
                <BodyText {...bodyProps} />
                {bodyProps.id === 6 && activeSection === 'stationOwner' && renderTariffsLink()}
              </React.Fragment>
            ))}
          </div>
        </div>
      </Container>
    </>
  );
}
